/**
 * Haptics (Options): short vibrations on game events. Only where the browser can vibrate
 * (Android browsers; iPhone and iPad browsers can't). Events from before the table mounted, or
 * from another game, never buzz.
 */
import { useEffect, useRef } from 'react';
import type { AttackView, GameView, PlayerId } from '@kadice/shared';

export const canVibrate = (): boolean => typeof navigator !== 'undefined' && typeof navigator.vibrate === 'function';

function buzz(pattern: number | readonly number[]): void {
  try {
    navigator.vibrate?.(typeof pattern === 'number' ? pattern : [...pattern]);
  } catch {
    // Blocked (no user gesture yet, or not allowed): a missed buzz isn't worth an error.
  }
}

/** Vibration patterns (ms on, off, on, ...). */
const PATTERNS = {
  yourTurn: 120,
  youKilled: [60, 40, 60],
  killedYou: [90, 60, 220],
  war: [40, 40, 40, 40, 40],
  wild: [50, 30, 50, 30, 160],
} as const;

interface Seen {
  gameId: number;
  turn: string;
  war: string;
  wild: number;
}

function seenOf(view: GameView): Seen {
  const me = view.you.id;
  const turn = view.phase === 'playing' ? view.turn : null;
  return {
    gameId: view.gameId,
    // Your turn starting: each turn has its own key (a Small Straight second turn and a Full House bonus roll too).
    turn:
      turn && turn.playerId === me && !turn.ghost ? `${view.round}:${view.sequence}:${turn.extra}:${turn.bonus}` : '',
    // A war you fight in (each war round; a tie rolls again).
    war: view.phase === 'war' && view.war?.participants.includes(me) ? `${view.war.cardId}:${view.war.round}` : '',
    wild: view.lastWild?.seq ?? 0,
  };
}

/**
 * A kill by you or on you buzzes at the moment the victim is sent flying, from the attack
 * animation (`useAttackAnimation` in GameTable; at once when there is no animation).
 */
export function buzzAttack(attack: AttackView, me: PlayerId): void {
  if (!canVibrate()) return;
  if (attack.victimIds.includes(me)) buzz(PATTERNS.killedYou);
  else if (attack.attackerId === me) buzz(PATTERNS.youKilled);
}

/** Buzz on your turn starting, a war you fight in, and anyone's five of a kind (kills: `buzzAttack`). */
export function useHaptics(view: GameView, on: boolean): void {
  const prev = useRef<Seen | null>(null);
  useEffect(() => {
    const now = seenOf(view);
    const before = prev.current;
    prev.current = now;
    if (!on || !canVibrate() || !before || before.gameId !== now.gameId || view.you.spectating) return;
    if (now.wild > before.wild) buzz(PATTERNS.wild);
    else if (now.war && now.war !== before.war) buzz(PATTERNS.war);
    else if (now.turn && now.turn !== before.turn) buzz(PATTERNS.yourTurn);
  }, [view, on]);
}
